/* eslint-disable jsdoc/check-param-names */
/* eslint-disable jsdoc/check-types */
/* eslint-disable jsdoc/require-param */
import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Divider,
} from "@mui/material";
import {
  Visibility,
  Face,
  Assignment,
  Security,
  Settings,
  NotificationsActive,
  Delete,
  Group,
} from "@material-ui/icons";

/**
 * A component that displays a card with various quick actions that can be
 * performed on a dealer, such as viewing their ID photo, contracts, and
 * changing their password. The component also displays a button to mark the
 * dealer as inactive or delete the dealer.
 *
 * The component takes in the dealer object, the current user object, and
 * several functions as props. The functions are used to handle the various
 * actions that can be performed on the dealer.
 *
 * @param {Object} dealer - The dealer object containing all information.
 * @param {Object} currentUser - The current user object containing all information.
 * @param {Function} onChangeLeaver - Function to handle click on mark as inactive button.
 * @param {Function} onDeleteClick - Function to handle click on delete button.
 * @param {Function} onChangePasswordClick - Function to handle click on change password button.
 * @param {Function} onContractsClick - Function to handle click on contracts button.
 * @param {Function} onAlertClick - Function to handle click on send alert button.
 * @param {Function} onSuppliersClick - Function to handle click on suppliers button.
 * @param {Function} onUserTypeClick - Function to handle click on change user type button.
 */
export default function QuickActionsCard({
  dealer,
  currentUser,
  onChangeLeaver,
  onDeleteClick,
  onChangePasswordClick,
  onContractsClick,
  onAlertClick,
  onSuppliersClick,
  onUserTypeClick,
}) {
  return (
    <Card elevation={2} sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Quick Actions
        </Typography>

        <Stack spacing={2}>
          <Button
            startIcon={<Visibility />}
            variant="outlined"
            fullWidth
            href={dealer.id_photo_url}
            target="_blank"
            sx={{ justifyContent: "flex-start", borderRadius: 2 }}
          >
            View ID Photo
          </Button>

          <Button
            startIcon={<Face />}
            variant="outlined"
            fullWidth
            href={dealer.person_photo_url}
            target="_blank"
            sx={{ justifyContent: "flex-start", borderRadius: 2 }}
          >
            View Headshot
          </Button>

          <Button
            startIcon={<Assignment />}
            variant="outlined"
            fullWidth
            onClick={onContractsClick}
            disabled
            sx={{ justifyContent: "flex-start", borderRadius: 2 }}
          >
            Contracts
          </Button>

          <Button
            startIcon={<Security />}
            variant="outlined"
            fullWidth
            onClick={onChangePasswordClick}
            sx={{ justifyContent: "flex-start", borderRadius: 2 }}
          >
            Change Password
          </Button>

          <Button
            startIcon={<Settings />}
            variant="outlined"
            fullWidth
            onClick={onSuppliersClick}
            sx={{ justifyContent: "flex-start", borderRadius: 2 }}
          >
            Suppliers & Product Types
          </Button>

          {currentUser?.is_admin && (
            <Button
              startIcon={<Group />}
              variant="outlined"
              fullWidth
              onClick={onUserTypeClick}
              sx={{ justifyContent: "flex-start", borderRadius: 2 }}
            >
              Change User Type
            </Button>
          )}

          {(currentUser?.is_admin || currentUser?.is_leader) && (
            <Button
              startIcon={<NotificationsActive />}
              variant="outlined"
              fullWidth
              onClick={onAlertClick}
              sx={{ justifyContent: "flex-start", borderRadius: 2 }}
            >
              Send Alert
            </Button>
          )}
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Stack spacing={2}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ fontWeight: 600 }}
          >
            Status Management
          </Typography>

          {dealer.refs_user_lifecycle_status_id === 3 ? (
            <Button
              variant="contained"
              color="success"
              fullWidth
              onClick={() => onChangeLeaver(false)}
              sx={{ borderRadius: 2 }}
            >
              Reactivate User
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="warning"
              fullWidth
              onClick={() => onChangeLeaver(true)}
              sx={{ borderRadius: 2 }}
            >
              Mark As Inactive
            </Button>
          )}

          <Button
            startIcon={<Delete />}
            variant="outlined"
            color="error"
            fullWidth
            onClick={onDeleteClick}
            sx={{ borderRadius: 2 }}
          >
            Delete User
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
